import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import "ag-grid-community/styles/ag-theme-quartz.css";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Center,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  Heading,
  HStack,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Text,
  Textarea,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { HiDocumentDuplicate, HiOutlineDownload, HiOutlineLockClosed, HiOutlinePlus, HiOutlineTrash, HiOutlineViewBoards } from "react-icons/hi";
import Navbar from "./Navbar";
import WatermarkBackground from "./components/WatermarkBackground";
import CodesetSelect from "./CodesetSelect";
import CodeListSidebar from "./components/codex/CodeListSidebar";
import PasteCodesModal from "./components/codex/PasteCodesModal";
import {
  CODE_FIELDS,
  applyPaste,
  changesSince,
  isLocked,
  newCode,
  newCodeList,
  toPayload,
  validate,
  withLocalIds,
} from "./components/codex/codexUtils";
import {
  copyCodeList,
  createCodeList,
  exportCodeListCsv,
  listCodeLists,
  listStandards,
  listStandardVersions,
  loadCodeList,
  updateCodeList,
} from "./services/CodexAPI";
import { showApiErrorToast } from "./utils/showApiToast";

ModuleRegistry.registerModules([AllCommunityModule]);

const COLUMNS_KEY = "mrc.codex.columns";
const DEFAULT_COLUMNS = CODE_FIELDS.filter((f) => f.main).map((f) => f.field);
const readColumns = () => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(COLUMNS_KEY) || "null");
    return Array.isArray(stored) && stored.includes("longCode") ? stored : DEFAULT_COLUMNS;
  } catch {
    return DEFAULT_COLUMNS;
  }
};
const saveColumns = (columns) => {
  try {
    window.localStorage.setItem(COLUMNS_KEY, JSON.stringify(columns));
  } catch {
    // Not remembered in this browser; the choice still applies now
  }
};

const ROW_COLOURS = { new: "#F0FFF4", changed: "#FFFAF0" };
// Tick boxes select rows (for Delete selected); clicking a row doesn't
const ROW_SELECTION = { mode: "multiRow", checkboxes: true, headerCheckbox: true, enableClickSelection: false };

/** Data standard and version, picked from Codex's standards (free text if it has none). */
const StandardFields = ({ value, version, onChange, isDisabled }) => {
  const [standards, setStandards] = useState(null);
  const [versions, setVersions] = useState([]);

  useEffect(() => {
    listStandards().then(setStandards).catch(() => setStandards([]));
  }, []);
  useEffect(() => {
    if (!value) return setVersions([]);
    listStandardVersions(value).then(setVersions).catch(() => setVersions([]));
  }, [value]);

  const free = standards !== null && standards.length === 0;
  const withCurrent = (options, current, key) =>
    current && !options.some((o) => o[key] === current) ? [{ [key]: current, name: current }, ...options] : options;

  return (
    <>
      <FormControl>
        <FormLabel fontSize="sm">Data standard</FormLabel>
        {free ? (
          <Input size="sm" bg="white" value={value || ""} isDisabled={isDisabled} onChange={(e) => onChange({ dataStandard: e.target.value })} />
        ) : (
          <Select size="sm" bg="white" value={value || ""} isDisabled={isDisabled || standards === null}
            onChange={(e) => onChange({ dataStandard: e.target.value, dataStandardVersion: "" })}>
            <option value="">—</option>
            {withCurrent(standards || [], value, "longCode").map((s) => (
              <option key={s.longCode} value={s.longCode}>{s.name && s.name !== s.longCode ? `${s.longCode} · ${s.name}` : s.longCode}</option>
            ))}
          </Select>
        )}
      </FormControl>
      <FormControl>
        <FormLabel fontSize="sm">Version</FormLabel>
        {free || (value && versions.length === 0) ? (
          <Input size="sm" bg="white" value={version || ""} isDisabled={isDisabled} onChange={(e) => onChange({ dataStandardVersion: e.target.value })} />
        ) : (
          <Select size="sm" bg="white" value={version || ""} isDisabled={isDisabled || !value} onChange={(e) => onChange({ dataStandardVersion: e.target.value })}>
            <option value="">—</option>
            {withCurrent(versions, version, "version").map((v) => (
              <option key={v.version} value={v.version}>{v.name && v.name !== v.version ? `${v.version} · ${v.name}` : v.version}</option>
            ))}
          </Select>
        )}
      </FormControl>
    </>
  );
};

/**
 * Codex: the platform's code lists. Pick a list on the left; edit its
 * details and codes on the right, then save. Unsaved changes are kept until
 * you save or discard, and you're asked before losing them.
 */
const Codex = () => {
  const toast = useToast();
  const gridRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("list") || "";

  const [lists, setLists] = useState([]);
  const [listsLoading, setListsLoading] = useState(true);
  const [listSearch, setListSearch] = useState("");

  const [original, setOriginal] = useState(null); // as loaded (null for a new list)
  const [codeList, setCodeList] = useState(null); // being edited
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [codeSearch, setCodeSearch] = useState("");
  const [columns, setColumns] = useState(readColumns);
  const [unlocked, setUnlocked] = useState(false);
  const [lastDeleted, setLastDeleted] = useState(null); // { rows, at: [index] }
  const [showProblems, setShowProblems] = useState(false);

  const copyModal = useDisclosure();
  const pasteModal = useDisclosure();
  const [copyId, setCopyId] = useState("");
  const [copying, setCopying] = useState(false);

  const isNew = !!codeList && !codeList._id;
  const changes = useMemo(() => changesSince(original, codeList), [original, codeList]);
  const problems = useMemo(() => validate(codeList), [codeList]);
  const locked = !!codeList && isLocked(codeList.locked) && isLocked(original?.locked);
  const canEdit = !!codeList && (!locked || unlocked);

  // The grid reads these through refs, so cell styles and editability stay current
  const live = useRef({});
  live.current = { rowState: changes.rowState, byRow: showProblems ? problems.byRow : {}, canEdit };

  const refreshLists = useCallback(async () => {
    try {
      setLists(await listCodeLists());
    } catch (err) {
      showApiErrorToast(toast, err, { title: "Couldn't load the code lists" });
    } finally {
      setListsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    refreshLists();
  }, [refreshLists]);

  const open = useCallback(
    async (tableId) => {
      if (!tableId) {
        setOriginal(null);
        setCodeList(null);
        return;
      }
      setLoading(true);
      try {
        const data = await loadCodeList(tableId);
        const loaded = { ...data, codes: withLocalIds(data?.codes) };
        setOriginal(loaded);
        setCodeList(loaded);
        setUnlocked(false);
        setLastDeleted(null);
        setShowProblems(false);
        setCodeSearch("");
      } catch (err) {
        showApiErrorToast(toast, err, { title: `Couldn't open ${tableId}` });
        setOriginal(null);
        setCodeList(null);
      } finally {
        setLoading(false);
      }
    },
    [toast]
  );

  // The list in the URL is the one shown (so a list can be linked to)
  useEffect(() => {
    if (selectedId && selectedId !== codeList?.tableID) open(selectedId);
    // only when the URL changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // Ask before leaving the page with unsaved changes
  useEffect(() => {
    if (!changes.isDirty) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [changes.isDirty]);

  useEffect(() => {
    const api = gridRef.current?.api;
    if (api) {
      api.redrawRows();
      api.refreshCells({ force: true });
    }
  }, [changes.rowState, showProblems, problems.byRow, canEdit]);

  const confirmDiscard = () =>
    !changes.isDirty || window.confirm(`Discard your unsaved changes to ${codeList?.tableID || "the new code list"}?`);

  const selectList = (tableId) => {
    if (tableId === codeList?.tableID && !isNew) return;
    if (!confirmDiscard()) return;
    setSearchParams({ list: tableId });
  };

  const startNew = () => {
    if (!confirmDiscard()) return;
    setSearchParams({});
    setOriginal(null);
    setCodeList(newCodeList());
    setUnlocked(false);
    setLastDeleted(null);
    setShowProblems(false);
  };

  const discard = () => {
    if (!window.confirm("Discard all unsaved changes?")) return;
    if (isNew) {
      setCodeList(null);
      return;
    }
    setCodeList(original);
    setLastDeleted(null);
    setShowProblems(false);
  };

  const setField = (patch) => setCodeList((cl) => ({ ...cl, ...patch }));
  const setCodes = (fn) => setCodeList((cl) => ({ ...cl, codes: fn(cl.codes || []) }));

  const addCode = () => {
    const code = newCode(codeList.tableID);
    setCodes((codes) => [...codes, code]);
    setTimeout(() => {
      const api = gridRef.current?.api;
      const node = api?.getRowNode(code.id);
      if (node) {
        api.ensureNodeVisible(node, "bottom");
        api.startEditingCell({ rowIndex: node.rowIndex, colKey: "longCode" });
      }
    }, 50);
  };

  const deleteSelected = () => {
    const ids = new Set((gridRef.current?.api?.getSelectedRows() || []).map((r) => r.id));
    if (!ids.size) return;
    const codes = codeList.codes || [];
    setLastDeleted({ rows: codes.map((c, i) => ({ c, i })).filter(({ c }) => ids.has(c.id)) });
    setCodes((list) => list.filter((c) => !ids.has(c.id)));
  };

  const undoDelete = () => {
    if (!lastDeleted) return;
    setCodes((list) => {
      const next = [...list];
      lastDeleted.rows.forEach(({ c, i }) => next.splice(Math.min(i, next.length), 0, c));
      return next;
    });
    setLastDeleted(null);
  };

  const save = async () => {
    setShowProblems(true);
    if (problems.messages.length) {
      return toast({ title: "Fix these before saving", description: problems.messages.join(" · "), status: "warning", duration: 6000, isClosable: true });
    }
    setSaving(true);
    try {
      const payload = toPayload(codeList);
      if (isNew) await createCodeList(payload);
      else await updateCodeList(codeList._id, payload);
      toast({ title: isNew ? "Code list created" : "Code list saved", description: `${codeList.tableID} · ${codeList.name}`, status: "success" });
      await refreshLists();
      // Reload what the API now holds
      const tableId = codeList.tableID.trim();
      setSearchParams({ list: tableId });
      await open(tableId);
    } catch (err) {
      showApiErrorToast(toast, err, { title: "Couldn't save the code list" });
    } finally {
      setSaving(false);
    }
  };

  const exportCsv = async () => {
    try {
      const csv = await exportCodeListCsv(codeList.tableID);
      const blob = new Blob([typeof csv === "string" ? csv : JSON.stringify(csv)], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${codeList.tableID}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      showApiErrorToast(toast, err, { title: "Couldn't export the code list" });
    }
  };

  const copy = async () => {
    const newId = copyId.trim();
    if (!newId) return;
    if (lists.some((l) => l.tableID === newId)) {
      return toast({ title: `${newId} already exists`, status: "warning" });
    }
    setCopying(true);
    try {
      await copyCodeList(codeList.tableID, newId);
      toast({ title: "Code list copied", description: `${codeList.tableID} → ${newId}`, status: "success" });
      copyModal.onClose();
      await refreshLists();
      setSearchParams({ list: newId });
    } catch (err) {
      showApiErrorToast(toast, err, { title: "Couldn't copy the code list" });
    } finally {
      setCopying(false);
    }
  };

  const columnDefs = useMemo(
    () => [
      ...CODE_FIELDS.filter((f) => columns.includes(f.field)).map((f, i) => ({
        headerName: f.label + (f.required ? " *" : ""),
        field: f.field,
        editable: () => live.current.canEdit,
        flex: f.field === "description1" ? 2 : 1,
        minWidth: f.field === "description1" ? 240 : 120,
        ...(i === 0 && { pinned: "left", minWidth: 160 }),
        tooltipValueGetter: (p) => live.current.byRow[p.data?.id]?.[f.field],
        cellStyle: (p) => (live.current.byRow[p.data?.id]?.[f.field] ? { backgroundColor: "#FED7D7" } : null),
      })),
    ],
    [columns]
  );

  const defaultColDef = useMemo(() => ({ sortable: true, filter: true, resizable: true }), []);
  const getRowStyle = useCallback((p) => {
    const state = live.current.rowState[p.data?.id];
    return state ? { backgroundColor: ROW_COLOURS[state] } : undefined;
  }, []);

  const onCellValueChanged = (p) => {
    const { id } = p.data || {};
    const field = p.colDef?.field;
    if (!id || !field) return;
    setCodes((codes) => codes.map((c) => (c.id === id ? { ...c, [field]: p.newValue } : c)));
  };

  const toggleColumns = (value) => {
    const next = ["longCode", ...(Array.isArray(value) ? value : [value]).filter((f) => f !== "longCode")];
    setColumns(next);
    saveColumns(next);
  };

  return (
    <Box position="relative" minHeight="100vh">
      <WatermarkBackground />
      <Navbar />
      <Box px={{ base: 4, md: 8 }} py={6} maxW="1800px" mx="auto">
        <HStack justify="space-between" mb={4}>
          <Box>
            <Heading size="lg">Codex</Heading>
            <Text fontSize="sm" color="gray.600">The platform's code lists and their codes</Text>
          </Box>
        </HStack>

        <Grid templateColumns={{ base: "1fr", lg: "300px 1fr" }} gap={4} alignItems="start">
          <Box h={{ lg: "calc(100vh - 190px)" }} position={{ lg: "sticky" }} top={4}>
            <CodeListSidebar
              lists={lists}
              loading={listsLoading}
              search={listSearch}
              onSearch={setListSearch}
              selectedId={isNew ? null : codeList?.tableID || selectedId}
              onSelect={selectList}
              onNew={startNew}
            />
          </Box>

          <VStack align="stretch" spacing={3} minW={0}>
            {loading ? (
              <Center p={10} borderWidth="1px" borderRadius="md" bg="whiteAlpha.800"><Spinner /></Center>
            ) : !codeList ? (
              <Center p={10} borderWidth="1px" borderRadius="md" bg="whiteAlpha.800">
                <Text color="gray.500">Choose a code list on the left, or create a new one.</Text>
              </Center>
            ) : (
              <>
                {/* Title and save bar */}
                <HStack borderWidth="1px" borderRadius="md" bg="whiteAlpha.800" px={4} py={3} justify="space-between" flexWrap="wrap" rowGap={2}>
                  <HStack spacing={3} minW={0}>
                    <Badge fontFamily="mono" fontSize="md" colorScheme="blue" px={2}>{codeList.tableID || "NEW"}</Badge>
                    <Heading size="md" noOfLines={1}>{codeList.name || "Untitled code list"}</Heading>
                    {locked && <Badge colorScheme="orange" display="flex" alignItems="center" gap={1}><HiOutlineLockClosed /> Locked</Badge>}
                    {changes.isDirty && (
                      <Badge colorScheme="yellow" textTransform="none">
                        Unsaved{changes.count ? ` · ${changes.count} code change${changes.count === 1 ? "" : "s"}` : ""}
                      </Badge>
                    )}
                  </HStack>
                  <HStack spacing={2}>
                    {!isNew && (
                      <>
                        <Tooltip label="Download as CSV" hasArrow>
                          <IconButton size="sm" variant="ghost" aria-label="Export CSV" icon={<HiOutlineDownload />} onClick={exportCsv} />
                        </Tooltip>
                        <Tooltip label="Copy to a new ID" hasArrow>
                          <IconButton size="sm" variant="ghost" aria-label="Copy to a new ID" icon={<HiDocumentDuplicate />}
                            onClick={() => { setCopyId(""); copyModal.onOpen(); }} isDisabled={changes.isDirty} />
                        </Tooltip>
                      </>
                    )}
                    {changes.isDirty && <Button size="sm" variant="ghost" onClick={discard}>Discard</Button>}
                    <Button size="sm" colorScheme="blue" onClick={save} isLoading={saving} isDisabled={!changes.isDirty || !canEdit}>
                      {isNew ? "Create" : "Save"}
                    </Button>
                  </HStack>
                </HStack>

                {locked && (
                  <Alert status="warning" borderRadius="md" variant="left-accent">
                    <AlertIcon />
                    <AlertDescription fontSize="sm" flex="1">
                      {unlocked
                        ? "This code list is locked. You're editing it anyway; other screens may rely on its codes."
                        : "This code list is locked, so it's read-only here."}
                    </AlertDescription>
                    {!unlocked && <Button size="xs" ml={3} onClick={() => setUnlocked(true)}>Edit anyway</Button>}
                  </Alert>
                )}

                {showProblems && problems.messages.length > 0 && (
                  <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription fontSize="sm">{problems.messages.join(" · ")}</AlertDescription>
                  </Alert>
                )}

                {/* The code list's details */}
                <Box borderWidth="1px" borderRadius="md" bg="whiteAlpha.800" p={4}>
                  <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={3}>
                    <FormControl isRequired gridColumn={{ xl: "span 2" }}>
                      <FormLabel fontSize="sm">Name</FormLabel>
                      <Input size="sm" bg="white" value={codeList.name || ""} isDisabled={!canEdit} onChange={(e) => setField({ name: e.target.value })} />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Code list ID</FormLabel>
                      <Input size="sm" bg="white" fontFamily="mono" value={codeList.tableID || ""} isReadOnly={!isNew}
                        placeholder="e.g. A82" onChange={(e) => setField({ tableID: e.target.value })} />
                      {!isNew && <FormHelperText fontSize="xs">Fixed: other screens look it up by ID. Copy it to use a new one.</FormHelperText>}
                    </FormControl>
                    <Box>
                      <CodesetSelect
                        label="Agency"
                        codesetCode="3055"
                        value={codeList.agency ? { code: codeList.agency, description: "" } : null}
                        onChange={(selected) => setField({ agency: selected?.code || "" })}
                        size="sm"
                      />
                    </Box>
                    <StandardFields value={codeList.dataStandard} version={codeList.dataStandardVersion} isDisabled={!canEdit} onChange={setField} />
                    <FormControl>
                      <FormLabel fontSize="sm">Filter</FormLabel>
                      <Input size="sm" bg="white" value={codeList.filter || ""} isDisabled={!canEdit} onChange={(e) => setField({ filter: e.target.value })} />
                    </FormControl>
                    <Box>
                      <CodesetSelect
                        label="Locked"
                        codesetCode="JVRI008"
                        value={codeList.locked ? { code: codeList.locked, description: "" } : null}
                        onChange={(selected) => setField({ locked: selected?.code || "" })}
                        size="sm"
                      />
                    </Box>
                    <FormControl gridColumn={{ md: "span 2", xl: "span 4" }}>
                      <FormLabel fontSize="sm">Notes</FormLabel>
                      <Textarea size="sm" bg="white" rows={2} value={codeList.notes || ""} isDisabled={!canEdit} onChange={(e) => setField({ notes: e.target.value })} />
                    </FormControl>
                  </SimpleGrid>
                </Box>

                {/* The codes */}
                <Box borderWidth="1px" borderRadius="md" bg="whiteAlpha.800">
                  <HStack px={3} py={2} justify="space-between" flexWrap="wrap" rowGap={2}>
                    <HStack spacing={2}>
                      <Button size="sm" leftIcon={<HiOutlinePlus />} onClick={addCode} isDisabled={!canEdit}>Add code</Button>
                      <Button size="sm" variant="outline" onClick={pasteModal.onOpen} isDisabled={!canEdit}>Paste from spreadsheet</Button>
                      <Button size="sm" variant="ghost" colorScheme="red" leftIcon={<HiOutlineTrash />} onClick={deleteSelected} isDisabled={!canEdit}>
                        Delete selected
                      </Button>
                      {lastDeleted && (
                        <Button size="sm" variant="link" colorScheme="blue" onClick={undoDelete}>
                          Undo delete ({lastDeleted.rows.length})
                        </Button>
                      )}
                    </HStack>
                    <HStack spacing={2}>
                      <Text fontSize="xs" color="gray.600">
                        {(codeList.codes || []).length} codes
                        {changes.added ? ` · ${changes.added} new` : ""}
                        {changes.changed ? ` · ${changes.changed} changed` : ""}
                        {changes.deleted ? ` · ${changes.deleted} deleted` : ""}
                      </Text>
                      <Input size="sm" w="220px" bg="white" placeholder="Search codes…" value={codeSearch} onChange={(e) => setCodeSearch(e.target.value)} />
                      <Menu closeOnSelect={false}>
                        <MenuButton as={Button} size="sm" variant="outline" leftIcon={<HiOutlineViewBoards />}>Columns</MenuButton>
                        <MenuList maxH="360px" overflowY="auto" fontSize="sm">
                          <MenuOptionGroup type="checkbox" value={columns} onChange={toggleColumns}>
                            {CODE_FIELDS.map((f) => (
                              <MenuItemOption key={f.field} value={f.field} isDisabled={f.field === "longCode"}>{f.label}</MenuItemOption>
                            ))}
                          </MenuOptionGroup>
                        </MenuList>
                      </Menu>
                    </HStack>
                  </HStack>
                  <Box className="ag-theme-quartz" h="62vh" w="100%">
                    <AgGridReact
                      ref={gridRef}
                      theme="legacy"
                      rowData={codeList.codes || []}
                      columnDefs={columnDefs}
                      defaultColDef={defaultColDef}
                      getRowId={(p) => p.data.id}
                      getRowStyle={getRowStyle}
                      rowSelection={ROW_SELECTION}
                      quickFilterText={codeSearch}
                      stopEditingWhenCellsLoseFocus
                      singleClickEdit={false}
                      tooltipShowDelay={200}
                      onCellValueChanged={onCellValueChanged}
                      overlayNoRowsTemplate="No codes yet. Add one, or paste them from a spreadsheet."
                    />
                  </Box>
                  <HStack px={3} py={2} spacing={4} fontSize="xs" color="gray.500">
                    <HStack spacing={1}><Box w="10px" h="10px" bg={ROW_COLOURS.new} borderWidth="1px" /><Text>new</Text></HStack>
                    <HStack spacing={1}><Box w="10px" h="10px" bg={ROW_COLOURS.changed} borderWidth="1px" /><Text>changed</Text></HStack>
                    <Text>Double-click a cell to edit. * required.</Text>
                  </HStack>
                </Box>
              </>
            )}
          </VStack>
        </Grid>
      </Box>

      <PasteCodesModal
        isOpen={pasteModal.isOpen}
        onClose={pasteModal.onClose}
        codes={codeList?.codes || []}
        onApply={(preview) => setCodes((codes) => applyPaste(codes, preview, codeList.tableID))}
      />

      <Modal isOpen={copyModal.isOpen} onClose={() => !copying && copyModal.onClose()} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Copy {codeList?.tableID} to a new ID</ModalHeader>
          <ModalCloseButton isDisabled={copying} />
          <ModalBody>
            <Text fontSize="sm" mb={3}>All of <strong>{codeList?.name}</strong>'s codes are copied to a new code list.</Text>
            <FormControl isRequired>
              <FormLabel fontSize="sm">New code list ID</FormLabel>
              <Input fontFamily="mono" value={copyId} onChange={(e) => setCopyId(e.target.value)} isDisabled={copying} autoFocus
                onKeyDown={(e) => e.key === "Enter" && copy()} />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={copyModal.onClose} isDisabled={copying}>Cancel</Button>
            <Button colorScheme="blue" onClick={copy} isLoading={copying} isDisabled={!copyId.trim()}>Copy</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Codex;
